import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer,
  IconButton, InputAdornment, MenuItem, Step, StepLabel, Stepper, TextField, Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { api, errorMessage } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { DataState } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import DataTable, { TwoLine } from "../components/DataTable";
import StatusChip from "../components/StatusChip";
import { TIER, TIER_ORDER, WORKFLOW } from "../constants";
import { formatDate } from "../utils/format";

const STEPS = ["AWAITING_RISK_ASSESSMENT", "RISK_ASSESSMENT_COMPLETED", "AWAITING_APPROVAL", "APPROVED"];

const EMPTY_FORM = {
  name: "", applicationType: "VENDOR", description: "", vendor: "", owner: "", businessUnit: "",
  useCase: "", modelType: "", dataSensitivity: "",
};

const TYPE = {
  INTERNAL: { label: "Internal build", color: "#0EA5E9" },
  VENDOR: { label: "Vendor-based", color: "#A855F7" },
};
const ASSESSMENT = {
  NOT_STARTED: { label: "Not started", color: "#94A3B8" },
  DRAFT: { label: "In progress", color: "#3B82F6" },
  SUBMITTED: { label: "Awaiting analyst review", color: "#8B5CF6" },
  RETURNED: { label: "Returned to owner", color: "#F59E0B" },
  COMPLETED: { label: "Completed", color: "#10B981" },
};

const tierChip = (app) => (
  <StatusChip label={app.riskTierLabel} color={TIER[app.riskTier || "UNASSIGNED"].color} />
);
const typeChip = (app) => <StatusChip label={TYPE[app.applicationType].label} color={TYPE[app.applicationType].color} />;
const statusChip = (app) => <StatusChip label={app.statusLabel} color={WORKFLOW[app.status].color} />;

function RegisterDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const internal = form.applicationType === "INTERNAL";
      await api.createApplication({
        ...form,
        vendor: internal ? "Internal" : form.vendor,
        deployment: internal ? "Internal build" : "Third-party SaaS",
      });
      setForm(EMPTY_FORM);
      onCreated();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Register an application</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          It starts in "Awaiting Risk Assessment". The owner answers a questionnaire and an analyst confirms the risk tier.
        </Typography>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, pt: 1 }}>
          <TextField label="Application name" required value={form.name} onChange={set("name")} sx={{ gridColumn: { sm: "1 / -1" } }} />
          <TextField select label="Where does it come from?" value={form.applicationType} onChange={set("applicationType")} sx={{ gridColumn: { sm: "1 / -1" } }}>
            <MenuItem value="VENDOR">Vendor-based (bought or licensed)</MenuItem>
            <MenuItem value="INTERNAL">Internal build</MenuItem>
          </TextField>
          <TextField label="What does it do?" multiline minRows={2} value={form.description} onChange={set("description")} sx={{ gridColumn: { sm: "1 / -1" } }} />
          {form.applicationType === "VENDOR" && <TextField label="Vendor" value={form.vendor} onChange={set("vendor")} />}
          <TextField label="Owner" value={form.owner} onChange={set("owner")} />
          <TextField label="Business unit" value={form.businessUnit} onChange={set("businessUnit")} />
          <TextField label="Use case" value={form.useCase} onChange={set("useCase")} />
          <TextField label="Model type" placeholder="LLM, predictive model…" value={form.modelType} onChange={set("modelType")} />
          <TextField select label="Most sensitive data" value={form.dataSensitivity} onChange={set("dataSensitivity")}>
            {["PHI", "PII", "Confidential", "Internal", "Public"].map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </TextField>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !form.name.trim()}>
          {busy ? "Saving…" : "Register"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Detail({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || "—"}</Typography>
    </Box>
  );
}

function ApplicationDrawer({ app, onClose, onChanged }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function move(status, riskTier) {
    setBusy(true);
    setError("");
    try {
      await api.updateApplicationStatus(app.id, { status, riskTier: riskTier || null });
      onChanged();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const stepIndex = Math.max(STEPS.indexOf(app.status), 0);

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, p: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography variant="h5">{app.name}</Typography>
          <Typography color="text.secondary">{app.applicationType === "VENDOR" ? app.vendor : "Built in-house"}</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close"><CloseRoundedIcon /></IconButton>
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {typeChip(app)}
        {statusChip(app)}
        {tierChip(app)}
      </Box>
      {app.description && <Typography sx={{ mb: 2 }}>{app.description}</Typography>}

      <Stepper activeStep={app.status === "REJECTED" ? -1 : stepIndex} alternativeLabel sx={{ my: 2 }}>
        {["Assess", "Assessed", "Approval", "Approved"].map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>
      {app.status === "REJECTED" && <Alert severity="error" sx={{ mb: 2 }}>This application was rejected.</Alert>}

      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <Detail label="Owner" value={app.owner} />
        <Detail label="Business unit" value={app.businessUnit} />
        <Detail label="Use case" value={app.useCase} />
        <Detail label="Model type" value={app.modelType} />
        <Detail label="Deployment" value={app.deployment} />
        <Detail label="Most sensitive data" value={app.dataSensitivity} />
        <Detail label="Last assessed" value={formatDate(app.lastAssessedOn)} />
        <Detail label="Next review due" value={formatDate(app.nextReviewDue)} />
      </Box>
      <Divider sx={{ my: 2.5 }} />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Next step</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {app.status === "AWAITING_RISK_ASSESSMENT" && (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Risk questionnaire: <b>{ASSESSMENT[app.assessmentStatus].label}</b>. The owner answers it, then an analyst
            confirms the risk tier.
          </Typography>
          <Button variant="contained" onClick={() => navigate(`/ai-inventory/${app.id}/assessment`)}>
            {app.assessmentStatus === "NOT_STARTED" ? "Start risk assessment"
              : app.assessmentStatus === "SUBMITTED" ? (me.canWrite ? "Review submission" : "View submission")
              : "Continue questionnaire"}
          </Button>
        </Box>
      )}
      {app.status !== "AWAITING_RISK_ASSESSMENT" && app.assessmentStatus !== "NOT_STARTED" && (
        <Button variant="outlined" sx={{ mb: 1.5 }} onClick={() => navigate(`/ai-inventory/${app.id}/assessment`)}>
          View risk assessment
        </Button>
      )}
      {app.status === "RISK_ASSESSMENT_COMPLETED" && me.canWrite && (
        <Button variant="contained" disabled={busy} onClick={() => move("AWAITING_APPROVAL")}>
          Submit for approval
        </Button>
      )}
      {app.status === "AWAITING_APPROVAL" && me.canApprove && (
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="contained" color="success" disabled={busy} onClick={() => move("APPROVED")}>Approve</Button>
          <Button variant="outlined" color="error" disabled={busy} onClick={() => move("REJECTED")}>Reject</Button>
        </Box>
      )}
      {(app.status === "APPROVED" || app.status === "REJECTED") && me.canWrite && (
        <Button variant="outlined" disabled={busy} onClick={() => move("AWAITING_RISK_ASSESSMENT")}>
          Reopen for a new risk assessment
        </Button>
      )}
      {!me.canWrite && app.status !== "AWAITING_RISK_ASSESSMENT" && (
        <Typography variant="body2" color="text.secondary">Your role can view applications but not change them.</Typography>
      )}
    </Drawer>
  );
}

export default function AiInventoryPage() {
  const { clientId, me, isAll, setClientId } = useSession();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [picked, setPicked] = useState(null); // { id, deptId }
  const state = useApiData(() => api.getApplications(), [clientId]);

  const tierFilter = params.get("tier") || "ALL";
  const statusFilter = params.get("status") || "ALL";
  const typeFilter = params.get("type") || "ALL";
  const deptFilter = params.get("dept") || "ALL";

  function setFilter(key, value) {
    const next = new URLSearchParams(params);
    if (value === "ALL") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (state.data || []).filter((a) => {
      if (tierFilter === "UNASSIGNED" && a.riskTier) return false;
      if (tierFilter !== "ALL" && tierFilter !== "UNASSIGNED" && a.riskTier !== tierFilter) return false;
      if (statusFilter !== "ALL" && a.status !== statusFilter) return false;
      if (typeFilter !== "ALL" && a.applicationType !== typeFilter) return false;
      if (deptFilter !== "ALL" && String(a.departmentId) !== deptFilter) return false;
      if (!q) return true;
      return [a.name, a.vendor, a.owner, a.businessUnit, a.useCase, a.department].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [state.data, search, tierFilter, statusFilter, typeFilter, deptFilter]);

  // Ids repeat between departments, so a row from the combined view is matched on its department too.
  const selected = picked && (state.data || []).find((a) => a.id === picked.id && (a.departmentId == null || a.departmentId === picked.deptId));

  // In the combined view a record belongs to one department: switch to it, then open the record.
  function openApplication(a) {
    if (isAll) setClientId(a.departmentId);
    setPicked({ id: a.id, deptId: a.departmentId });
  }

  const columns = [
    { key: "name", label: "Application", render: (a) => <TwoLine main={a.name} sub={a.applicationType === "VENDOR" ? a.vendor : "Built in-house"} /> },
    { key: "applicationType", label: "Source", render: typeChip },
    { key: "owner", label: "Owner", render: (a) => <TwoLine main={a.owner} sub={a.businessUnit} /> },
    { key: "dataSensitivity", label: "Data" },
    // Tier 1 is the most critical, so ascending puts the riskiest first and unrated last.
    { key: "riskTier", label: "Risk tier", render: tierChip },
    { key: "status", label: "Stage", render: statusChip, sortValue: (a) => Object.keys(WORKFLOW).indexOf(a.status) },
    {
      key: "assessmentStatus", label: "Questionnaire",
      sortValue: (a) => Object.keys(ASSESSMENT).indexOf(a.assessmentStatus),
      render: (a) => <StatusChip label={ASSESSMENT[a.assessmentStatus].label} color={ASSESSMENT[a.assessmentStatus].color} />,
    },
    { key: "nextReviewDue", label: "Next review", nowrap: true, render: (a) => formatDate(a.nextReviewDue) },
  ];

  return (
    <>
      <PageHeader
        title="Application Inventory"
        subtitle="Every application, internal or vendor-based: its risk tier, questionnaire and where it is in the approval process."
        actions={
          me.canWrite && !isAll && (
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setRegisterOpen(true)}>
              Register application
            </Button>
          )
        }
      />
      <DataState state={state}>
        {() => (
          <SectionCard>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2.5, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search name, vendor, owner…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 260 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
              />
              <TextField select size="small" label="Stage" value={statusFilter} onChange={(e) => setFilter("status", e.target.value)} sx={{ minWidth: 220 }}>
                <MenuItem value="ALL">All stages</MenuItem>
                {Object.entries(WORKFLOW).map(([key, v]) => <MenuItem key={key} value={key}>{v.label}</MenuItem>)}
              </TextField>
              {isAll && (
                <TextField select size="small" label="Department" value={deptFilter} onChange={(e) => setFilter("dept", e.target.value)} sx={{ minWidth: 180 }}>
                  <MenuItem value="ALL">All departments</MenuItem>
                  {[...new Map((state.data || []).map((a) => [a.departmentId, a.department])).entries()].map(([id, name]) => (
                    <MenuItem key={id} value={String(id)}>{name}</MenuItem>
                  ))}
                </TextField>
              )}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {["ALL", "INTERNAL", "VENDOR"].map((key) => (
                  <Chip
                    key={key}
                    label={key === "ALL" ? "All sources" : TYPE[key].label}
                    clickable
                    color={typeFilter === key ? "primary" : "default"}
                    variant={typeFilter === key ? "filled" : "outlined"}
                    onClick={() => setFilter("type", key)}
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {["ALL", ...TIER_ORDER, "UNASSIGNED"].map((key) => (
                  <Chip
                    key={key}
                    label={key === "ALL" ? "All tiers" : TIER[key].short}
                    clickable
                    color={tierFilter === key ? "primary" : "default"}
                    variant={tierFilter === key ? "filled" : "outlined"}
                    onClick={() => setFilter("tier", key)}
                  />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
                {rows.length} of {state.data.length}
              </Typography>
            </Box>
            <DataTable columns={columns} rows={rows} onRowClick={openApplication} defaultSort={{ key: "riskTier", dir: "asc" }} emptyText="No applications match these filters." />
          </SectionCard>
        )}
      </DataState>

      {selected && (
        <ApplicationDrawer app={selected} onClose={() => setPicked(null)} onChanged={state.reload} />
      )}
      <RegisterDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onCreated={() => {
          setRegisterOpen(false);
          state.reload();
        }}
      />
    </>
  );
}
